import React from "react";
import { formatWithDecimals } from "utils";
import { CurrencyFlagRound, CryptoIcon } from "ui";
import styles from "./DisplayAmount.module.css";
import { Flex, Typography } from "antd";
import { contractCurrencyName, TOKEN_BALANCE_ENUM } from "interfaces";

import "styles/imageStyles.css";

const CRYPTO_CURRENCIES = new Set<string>(Object.values(TOKEN_BALANCE_ENUM));
const THIN_SPACE = String.fromCharCode(8201);
const DEFAULT_ICON_SIZE = "3xl";

interface DisplayAmountProps {
  amount: number;
  currency: string;
  decimals?: number;
  showFlag?: boolean;
  bold?: boolean;
  showCurrency?: boolean;
  className?: string;
  squareBg?: boolean;
  withName?: boolean;
  currencyList?: { [key: string]: string } | undefined;
  isCurrencyGrey?: boolean;
  iconSize?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
}

const getCurrencyName = (currency: string, currencyList?: DisplayAmountProps["currencyList"]) => {
  if (currencyList?.[currency]) return currencyList[currency];
  if (CRYPTO_CURRENCIES.has(currency)) return contractCurrencyName[currency as keyof typeof contractCurrencyName];
  return currency;
};

const areDisplayAmountPropsEqual = (prevProps: DisplayAmountProps, nextProps: DisplayAmountProps) => {
  if (
    prevProps.amount !== nextProps.amount ||
    prevProps.currency !== nextProps.currency ||
    prevProps.decimals !== nextProps.decimals ||
    prevProps.showFlag !== nextProps.showFlag ||
    prevProps.bold !== nextProps.bold ||
    prevProps.showCurrency !== nextProps.showCurrency ||
    prevProps.className !== nextProps.className ||
    prevProps.squareBg !== nextProps.squareBg ||
    prevProps.withName !== nextProps.withName ||
    prevProps.isCurrencyGrey !== nextProps.isCurrencyGrey ||
    prevProps.iconSize !== nextProps.iconSize
  ) {
    return false;
  }

  if (!nextProps.withName) {
    return true;
  }

  return prevProps.currencyList?.[prevProps.currency] === nextProps.currencyList?.[nextProps.currency];
};

export const DisplayAmount: React.FC<DisplayAmountProps> = React.memo(({
  amount,
  currency,
  decimals = 2,
  showFlag,
  bold,
  showCurrency = true,
  className,
  squareBg,
  withName = false,
  isCurrencyGrey = false,
  currencyList,
  iconSize,
}) => {
  const formattedAmount = formatWithDecimals(amount, decimals);
  const isCrypto = CRYPTO_CURRENCIES.has(currency);
  const currencyName = getCurrencyName(currency, currencyList);
  const textClassName = bold ? "text-bold" : undefined;
  const containerClassName = className ? `${styles.amountContainer} ${className}` : styles.amountContainer;
  const cryptoIconSize = iconSize ?? DEFAULT_ICON_SIZE;

  const amountContent = (
    <Flex vertical gap="xsmall">
      <Flex align="center">
        <Typography.Text className={textClassName}>{`${formattedAmount}${THIN_SPACE}`}</Typography.Text>
        {showCurrency && (
          <Typography.Text className={textClassName} type={isCurrencyGrey ? "secondary" : undefined}>
            {currency}
          </Typography.Text>
        )}
      </Flex>
      {withName && <Typography.Text type="secondary">{currencyName}</Typography.Text>}
    </Flex>
  );

  if (showFlag) {
    return (
      <Flex className={containerClassName}>
        {isCrypto ? (
          <CryptoIcon currency={currency} squareBg={squareBg} squareSize={cryptoIconSize} />
        ) : (
          <CurrencyFlagRound currency={currency} squareBg={squareBg} />
        )}
        {amountContent}
      </Flex>
    );
  }

  return amountContent;
}, areDisplayAmountPropsEqual);
